import { List as ImmutableList } from 'immutable';
import { createSelector } from 'reselect';
import { INVESTMENT_IRR_REDUCER_KEY } from './investmentIRRReducer';

export const getResourceStore = (state) => state[INVESTMENT_IRR_REDUCER_KEY];

export const getLoadPending = (state) => getResourceStore(state).loadPending;
export const getIsLoading = (state) => getResourceStore(state).isLoading;
export const getError = (state) => getResourceStore(state).error;
export const getResourcesById = (state) => getResourceStore(state).resourcesById;
export const getLastSyncDate = (state) => getResourceStore(state).lastSyncDate;

const doNotIncludeStatus = {
  NOT_ENOUGH_DATAPOINTS: true,
  NOT_CONVERGED: true,
  INVALID_DATAPOINTS: true,
  UNSUCCESSFUL: true,
};

const statusMessages = {
  NOT_ENOUGH_DATAPOINTS: 'We are unable to calculate IRR for this account',
  NOT_CONVERGED: 'We are unable to calculate IRR for this account',
  INVALID_DATAPOINTS: 'We are unable to calculate IRR for this account',
  UNSUCCESSFUL: 'We are unable to calculate IRR for this account',
};

/*
 * Get the chart graph color for data
 */
const getColorForAccount = (tempIndex, colors) => {
  const currIndex = tempIndex % colors?.length;
  if (!colors?.colorList) return;
  return colors?.colorList[currIndex];
};

/*
 * Adjust the list of objects so that it excludes the 'total' and changes property names to fit the chart data
 * */
export const getPerformancesForBarChart = createSelector(
  (state, colors) => colors,
  getResourcesById,
  (colors, performances) => {
    const reformattedPerformances = performances
      .map((account) => ({
        accountId: account.accountId,
        id: account.id,
        category: account.accountName,
        totalPerformance: account.totalPerformance,
        status: account.status,
      }))
      .toList()
      .toArray();

    //  Filter out total
    const performancesWithoutTotal = reformattedPerformances.filter((account) => account.category !== 'Total');

    const performancesWithColor = performancesWithoutTotal.map((account, index) => ({
      ...account,
      color: getColorForAccount(index, colors),
    }));

    // Filter out undefined values
    const performancesWithoutUndefined = performancesWithColor.filter(
      (account) => typeof account.totalPerformance === 'number',
    );

    // Filter out accounts with status NOT_ENOUGH_DATAPOINTS
    const performancesWithDatapoints = performancesWithoutUndefined.filter(
      (account) => !doNotIncludeStatus[account.status],
    );

    //  const sortedList = performancesWithoutTotal.sort((a, b) => b.dailyPerformance.length - a.dailyPerformance.length);

    return ImmutableList(performancesWithDatapoints);
  },
);

/*
 * The same as the above selector but does not exclude undefined values
 * */
export const getPerformancesForTable = createSelector(
  (state, colors) => colors,
  getResourcesById,
  (colors, performances) => {
    const reformattedPerformances = performances
      .map((account) => ({
        accountId: account.accountId,
        id: account.id,
        category: account.accountName,
        totalPerformance: doNotIncludeStatus[account.status] ? null : account.totalPerformance,
        status: doNotIncludeStatus[account.status] ? account.status : null,
        statusMessage: statusMessages[account.status] || null,
      }))
      .toList()
      .toArray();

    //  Filter out total
    const performancesWithoutTotal = reformattedPerformances.filter((account) => account.category !== 'Total');

    const performancesWithColor = performancesWithoutTotal.map((account, index) => ({
      ...account,
      color: getColorForAccount(index, colors),
    }));

    return ImmutableList(performancesWithColor);
  },
);

export const getPerformanceTotal = createSelector(getResourcesById, (performances) => {
  const performancesTotal = performances.filter((account) => account.accountName === 'Total');
  return typeof performancesTotal.toList().toArray()[0]?.totalPerformance === 'number'
    ? performancesTotal.toList().toArray()[0]?.totalPerformance
    : null;
});
